<template>
  <PanelPage ref="useAlerts" :loading-overlay="loadingOverlay">
    <v-card>
      <v-card-title>Create User</v-card-title>
      <v-form @submit.prevent="submit" @reset="handleReset">
        <v-card-text class="pb-0">
          <v-text-field
            v-model="form.username.value.value"
            :error-messages="form.username.errorMessage.value"
            label="Username"
          />
          <v-text-field
            v-model="form.email.value.value"
            :error-messages="form.email.errorMessage.value"
            label="Email"
          />
          <v-text-field
            v-model="form.password.value.value"
            :error-messages="form.password.errorMessage.value"
            label="Password"
            :append-icon="showpwd ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showpwd ? 'text' : 'password'"
            @click:append="showpwd = !showpwd"
          />
          <v-text-field
            v-model="form.repassword.value.value"
            :error-messages="form.repassword.errorMessage.value"
            label="Re-insert password"
            :append-icon="showpwd ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showpwd ? 'text' : 'password'"
            @click:append="showpwd = !showpwd"
          />
          <v-checkbox
            v-model="form.is_staff.value.value"
            :error-messages="form.is_staff.errorMessage.value"
            label="RossiniEnergy Staff"
            color="red"
            hide-details
          />
        </v-card-text>
        <!-- TODO: missing Add manually Enabled CS -->
        <v-card-text>
          <v-card-title>ParkAdmin Feature</v-card-title>
          <v-checkbox label="Enable" v-model="parkadminenable"/>
          <div v-show="parkadminenable">
            <v-autocomplete
              label="Managed Parks"
              :items="parks"
              multiple
              chips
              v-model="form.managedparkids.value.value"
              :error-messages="form.managedparkids.errorMessage.value"
              item-title="name"
              item-value="id"
            />
            <v-autocomplete
              label="Child Users"
              :items="users"
              multiple
              chips
              v-model="form.childuserids.value.value"
              :error-messages="form.childuserids.errorMessage.value"
              item-title="username"
              item-value="id"
            />
            <v-card elevation="4">
              <v-card-title>
                <v-checkbox
                  label="ChargeMap Business Integration"
                  v-model="form.cmb_feature.value.value"
                  hide-details
                />
              </v-card-title>
              <v-card-text>
                <v-text-field
                  v-model="form.cmb_org_id.value.value"
                  :error-messages="form.cmb_org_id.errorMessage.value"
                  type="number"
                  label="Organization ID"
                  :disabled="!form.cmb_feature.value.value"
                />
                <v-text-field
                  v-model="form.cmb_token_id.value.value"
                  :error-messages="form.cmb_token_id.errorMessage.value"
                  label="Token ID"
                  :disabled="!form.cmb_feature.value.value"
                />
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn type="reset">Reset</v-btn>
          <v-btn type="submit">Submit</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </PanelPage>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useForm } from 'vee-validate';
import { getFormFromSchema } from "../utils.js";
import * as yup from 'yup';
import PanelPage from "../components/PanelPage.vue";
import { createUser, fetchUsers } from "../remote/user.js";
import { fetchParks } from "../remote/park.js";

// State
const useAlerts = ref(null);
const loadingOverlay = ref(false);
const showpwd = ref(false);
const parkadminenable = ref(false);

// Data
const parks = ref([]);
const users = ref([]);

// Schema
// noinspection JSCheckFunctionSignatures
const schema = yup.object({
  username: yup.string().required(),
  email: yup.string().email().required(),
  password: yup.string().required(),
  repassword: yup.string().oneOf([yup.ref('password'), null], "Passwords do not match").required(),
  childuserids: yup.array(),
  managedparkids: yup.array(),
  is_staff: yup.boolean(),
  cmb_feature: yup.boolean(),
  cmb_org_id: yup.number().when(
    "cmb_feature", {
      is: true,
      then: (schema) => schema.required().min(1),
      otherwise: (schema) => schema.notRequired(),
    }),
  cmb_token_id: yup.string().when(
    "cmb_feature", {
      is: true,
      then: (schema) => schema.required().min(36),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const { handleSubmit, handleReset } = useForm({
  validationSchema: schema
});

const form = getFormFromSchema(schema);

// noinspection JSValidateTypes
const submit = handleSubmit(async (values) => {
  let payload = {
    username: values.username,
    email: values.email,
    password: values.password,
    is_staff: values.is_staff,
    features: {
      charge: {
        authorized_cs: []
      }
    }
  };
  if (parkadminenable.value) {
    payload.features.parkadmin = {};
    if (values.managedparkids) {
      payload.features.parkadmin.managed_parks = values.managedparkids;
    }
    if (values.childuserids) {
      payload.features.parkadmin.child_users = values.childuserids;
    }
    payload.features.parkadmin.cmb_feature = values.cmb_feature ?? false;
    if (values.cmb_feature ?? false) {
      payload.features.parkadmin.cmb_org_id = values.cmb_org_id;
      payload.features.parkadmin.cmb_token_id = values.cmb_token_id;
    }
  }
  loadingOverlay.value = true;
  // Create user
  try {
    await createUser(useAlerts, payload);
  } catch (e) {
    useAlerts.value.errorAlert('Error during User creation', e.toString());
  } finally {
    loadingOverlay.value = false;
  }
});

onMounted(async () => {
  loadingOverlay.value = true;
  try {
    [parks.value, users.value] = await Promise.all([fetchParks(), fetchUsers()]);
  } catch (e) {
    useAlerts.value.errorAlert('Impossible to load initial infos', e.toString());
  } finally {
    loadingOverlay.value = false;
  }
});
</script>

<style scoped>

</style>
